/**
 * The live `ChordRecognizer`: microphone → Web Audio graph → DSP core → events.
 *
 * Everything platform-specific and awkward is concentrated here so that neither the
 * DSP core nor the session logic has to know about it. In particular:
 *
 * ## iOS is the whole reason this file is not twenty lines
 *
 *  - **The context must be resumed inside a user gesture.** `new AudioContext()` starts
 *    `suspended` on iOS, and `resume()` only succeeds while a tap is being handled.
 *    `start()` is therefore designed to be called directly from the start button's
 *    handler, and it awaits `resume()` before doing anything else. `start(null)` is
 *    supported precisely so the context can be created and resumed during the tap that
 *    *precedes* the microphone prompt.
 *  - **Interruptions really happen.** Siri, an incoming call, the app being
 *    backgrounded, another tab taking the input — all of them suspend the context, and
 *    Safari has a non-standard `'interrupted'` state that is not in the spec's
 *    `AudioContextState` union. We listen for `statechange` *and* `visibilitychange`
 *    and re-resume opportunistically, and we watch the track's `readyState` because an
 *    interruption sometimes ends the track outright, which no amount of `resume()`
 *    will fix — that needs a fresh `getUserMedia` from a fresh gesture, which is why
 *    `onStatus` reports `trackEnded`.
 *  - **The sample rate is whatever the hardware says.** iOS gives 48000 on some
 *    devices, 44100 on others, and can change it when a Bluetooth device connects. It
 *    is read from `AudioContext.sampleRate` after `resume()` and passed to the core;
 *    it is never assumed anywhere.
 *  - **AudioWorklet is not guaranteed.** It is well supported in modern iOS Safari, but
 *    this is the riskiest platform in the project and `addModule` can fail for reasons
 *    ranging from a MIME type to a lockdown-mode setting. `ScriptProcessorNode` is
 *    deprecated, runs on the main thread, and is measurably worse — and it still works
 *    everywhere. It is wired behind the identical interface as an escape hatch; set
 *    `forceScriptProcessor` (or flip the toggle on `/dev/tuning`) to compare the two
 *    paths against the same piano without a rebuild.
 *
 * ## Stream ownership
 *
 * `stop()` disconnects the graph and closes the context. It does **not** stop the
 * media tracks — `micManager` owns those. Recognition can therefore stop and start
 * without the iOS microphone indicator flickering and without re-prompting.
 */

import {
  DEFAULT_RECOGNIZER_PARAMS,
  mergeRecognizerParams,
  type ChordRecognizer,
  type MediaStreamLike,
  type RecognizerEvent,
  type RecognizerParams,
} from '@hc/shared';
import {
  loadRecognizerCore,
  type LoadedCore,
  type RecognizerCore,
  type RecognizerCoreOptions,
} from './coreAdapter.js';

/** Must match the name registered inside `forwarder.worklet.ts`. */
export const FORWARDER_PROCESSOR_NAME = 'hc-forwarder';

/** Frames per call into the core. See the note in the worklet on why 512. */
export const DEFAULT_BLOCK_SIZE = 512;

/**
 * ScriptProcessor buffer size. Must be a power of two, 256…16384. 4096 is the smallest
 * value that does not glitch on a busy main thread on an iPad; the block is re-chunked
 * to `blockSize` afterwards so the core sees identical input on both paths.
 */
export const SCRIPT_PROCESSOR_BUFFER_SIZE = 4096;

export type AudioPath = 'worklet' | 'script-processor';

/** `'interrupted'` is Safari-only and absent from the spec's `AudioContextState`. */
export type LiveContextState = 'uninitialised' | AudioContextState | 'interrupted';

export interface LiveRecognizerStatus {
  running: boolean;
  paused: boolean;
  contextState: LiveContextState;
  /** From `AudioContext.sampleRate`. `null` before `start()`. */
  sampleRate: number | null;
  path: AudioPath | null;
  coreKind: LoadedCore['kind'] | null;
  coreDetail: string | null;
  blockSize: number;
  blocksProcessed: number;
  /** `performance.now()` of the most recent block. Stale value ⇒ audio has stopped. */
  lastBlockAt: number | null;
  /** True once the OS has taken the input away. Needs a fresh `getUserMedia`. */
  trackEnded: boolean;
  /** How many times we have had to re-resume after an interruption. */
  resumeCount: number;
  lastError: string | null;
}

interface VisibilityTargetLike {
  visibilityState?: string;
  addEventListener(type: string, listener: () => void): void;
  removeEventListener(type: string, listener: () => void): void;
}

export interface LiveRecognizerOptions {
  createAudioContext?: () => AudioContext;
  loadCore?: (options: RecognizerCoreOptions) => Promise<LoadedCore>;
  loadWorkletUrl?: () => Promise<string>;
  /** Skip AudioWorklet entirely and use the deprecated fallback. */
  forceScriptProcessor?: boolean;
  blockSize?: number;
  initialParams?: Partial<RecognizerParams>;
  /** Defaults to `globalThis.document`. Pass `null` to disable visibility handling. */
  visibilityTarget?: VisibilityTargetLike | null;
}

export interface LiveRecognizer extends ChordRecognizer {
  getStatus(): LiveRecognizerStatus;
  onStatus(listener: (status: LiveRecognizerStatus) => void): () => void;
  /**
   * Best-effort resume, for a "tap to resume listening" button. Safe to call from a
   * gesture handler after an interruption.
   */
  tryResumeContext(): Promise<boolean>;
}

interface AudioContextCtor {
  new (options?: AudioContextOptions): AudioContext;
}

function defaultCreateAudioContext(): AudioContext {
  const scope = globalThis as unknown as {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  const Ctor = scope.AudioContext ?? scope.webkitAudioContext;
  if (!Ctor) throw new Error('Web Audio is not available in this browser.');
  // `latencyHint: 'interactive'` asks for the smallest buffer the device will give us.
  // We do not request a sampleRate: forcing one on iOS causes resampling we cannot see.
  return new Ctor({ latencyHint: 'interactive' });
}

function defaultVisibilityTarget(): VisibilityTargetLike | null {
  const scope = globalThis as unknown as { document?: VisibilityTargetLike };
  return scope.document ?? null;
}

function isRealMediaStream(value: MediaStreamLike | null): value is MediaStreamLike & MediaStream {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { getAudioTracks?: unknown }).getAudioTracks === 'function'
  );
}

/**
 * Accumulates arbitrary-sized input into fixed `size` blocks.
 *
 * The worklet already emits exact blocks; the ScriptProcessor path does not. Running
 * both through the same re-chunker is what makes tuning results from the fallback path
 * comparable with results from the worklet path.
 */
class Rechunker {
  private buffer: Float32Array;
  private fill = 0;

  constructor(private readonly size: number) {
    this.buffer = new Float32Array(size);
  }

  push(input: Float32Array, emit: (block: Float32Array) => void): void {
    let offset = 0;
    while (offset < input.length) {
      const take = Math.min(this.size - this.fill, input.length - offset);
      this.buffer.set(input.subarray(offset, offset + take), this.fill);
      this.fill += take;
      offset += take;
      if (this.fill === this.size) {
        const block = this.buffer;
        this.buffer = new Float32Array(this.size);
        this.fill = 0;
        emit(block);
      }
    }
  }

  reset(): void {
    this.fill = 0;
  }
}

export function createLiveRecognizer(options: LiveRecognizerOptions = {}): LiveRecognizer {
  const createAudioContext = options.createAudioContext ?? defaultCreateAudioContext;
  const loadCore = options.loadCore ?? loadRecognizerCore;
  const loadWorkletUrl =
    options.loadWorkletUrl ?? (async () => (await import('./worklet/url.js')).default);
  const blockSize = options.blockSize ?? DEFAULT_BLOCK_SIZE;
  const visibilityTarget =
    options.visibilityTarget === undefined ? defaultVisibilityTarget() : options.visibilityTarget;

  let params = mergeRecognizerParams(DEFAULT_RECOGNIZER_PARAMS, options.initialParams ?? {});
  let core: RecognizerCore | null = null;
  let loaded: LoadedCore | null = null;

  let context: AudioContext | null = null;
  let source: MediaStreamAudioSourceNode | null = null;
  let workletNode: AudioWorkletNode | null = null;
  let scriptNode: ScriptProcessorNode | null = null;
  let sink: GainNode | null = null;
  let path: AudioPath | null = null;
  let attachedStream: MediaStream | null = null;

  let running = false;
  let paused = false;
  let blocksProcessed = 0;
  let lastBlockAt: number | null = null;
  let trackEnded = false;
  let resumeCount = 0;
  let lastError: string | null = null;
  let resumeInFlight = false;
  let startInFlight: Promise<void> | null = null;

  const rechunker = new Rechunker(blockSize);
  const eventListeners = new Set<(event: RecognizerEvent) => void>();
  const statusListeners = new Set<(status: LiveRecognizerStatus) => void>();
  const teardown: Array<() => void> = [];

  function now(): number {
    return typeof performance !== 'undefined' ? performance.now() : Date.now();
  }

  function contextState(): LiveContextState {
    if (!context) return 'uninitialised';
    return context.state as LiveContextState;
  }

  function status(): LiveRecognizerStatus {
    return {
      running,
      paused,
      contextState: contextState(),
      sampleRate: context?.sampleRate ?? null,
      path,
      coreKind: loaded?.kind ?? null,
      coreDetail: loaded?.detail ?? null,
      blockSize,
      blocksProcessed,
      lastBlockAt,
      trackEnded,
      resumeCount,
      lastError,
    };
  }

  function publishStatus(): void {
    const snapshot = status();
    for (const listener of statusListeners) listener(snapshot);
  }

  function emit(event: RecognizerEvent): void {
    for (const listener of eventListeners) listener(event);
  }

  function handleBlock(samples: Float32Array): void {
    if (paused || !core) return;
    blocksProcessed += 1;
    lastBlockAt = now();
    let events: RecognizerEvent[];
    try {
      events = core.processFrame(samples);
    } catch (error) {
      // A throwing core must not kill the audio graph — it would take the session with
      // it. Record it, drop the block, keep listening.
      lastError = error instanceof Error ? error.message : String(error);
      publishStatus();
      return;
    }
    for (const event of events) emit(event);
  }

  async function tryResumeContext(): Promise<boolean> {
    const ctx = context;
    if (!ctx || resumeInFlight) return false;
    // Read into a local: `state` is mutated by the platform, and TypeScript would
    // otherwise keep the pre-`await` narrowing across the resume.
    const before: LiveContextState = ctx.state;
    if (before === 'running') return true;
    if (before === 'closed') return false;
    resumeInFlight = true;
    try {
      await ctx.resume();
      resumeCount += 1;
      publishStatus();
      const after: LiveContextState = ctx.state;
      return after === 'running';
    } catch (error) {
      // Expected when we are outside a user gesture. The UI offers a tap-to-resume.
      lastError = error instanceof Error ? error.message : String(error);
      publishStatus();
      return false;
    } finally {
      resumeInFlight = false;
    }
  }

  function watchContext(ctx: AudioContext): void {
    const onStateChange = () => {
      publishStatus();
      if (!running || paused) return;
      const state = ctx.state as LiveContextState;
      if (state === 'suspended' || state === 'interrupted') void tryResumeContext();
    };
    ctx.addEventListener('statechange', onStateChange);
    teardown.push(() => ctx.removeEventListener('statechange', onStateChange));
  }

  function watchVisibility(): void {
    if (!visibilityTarget) return;
    const onVisible = () => {
      if (visibilityTarget.visibilityState === 'hidden') return;
      if (!running || paused) return;
      checkTracks();
      void tryResumeContext();
    };
    visibilityTarget.addEventListener('visibilitychange', onVisible);
    teardown.push(() => visibilityTarget.removeEventListener('visibilitychange', onVisible));
  }

  function checkTracks(): void {
    if (!attachedStream) return;
    const dead = attachedStream.getAudioTracks().every((track) => track.readyState === 'ended');
    if (dead && !trackEnded) {
      trackEnded = true;
      lastError = 'The microphone input ended — reacquire it from a user gesture.';
      publishStatus();
    }
  }

  function watchTracks(stream: MediaStream): void {
    for (const track of stream.getAudioTracks()) {
      const onEnded = () => {
        if (attachedStream !== stream) return;
        trackEnded = true;
        publishStatus();
      };
      track.addEventListener('ended', onEnded);
      teardown.push(() => track.removeEventListener('ended', onEnded));
    }
  }

  async function buildWorkletPath(ctx: AudioContext): Promise<boolean> {
    if (options.forceScriptProcessor) return false;
    if (!ctx.audioWorklet || typeof ctx.audioWorklet.addModule !== 'function') return false;
    try {
      const url = await loadWorkletUrl();
      await ctx.audioWorklet.addModule(url);
      const node = new AudioWorkletNode(ctx, FORWARDER_PROCESSOR_NAME, {
        numberOfInputs: 1,
        // One output, connected to a muted gain into the destination. A node with zero
        // outputs is not reliably pulled by every implementation; this costs nothing
        // and removes an entire category of "it works everywhere except Safari".
        numberOfOutputs: 1,
        outputChannelCount: [1],
        channelCount: 1,
        channelCountMode: 'explicit',
        processorOptions: { blockSize },
      });
      node.port.onmessage = (event: MessageEvent) => {
        const data = event.data as { type?: string; samples?: Float32Array } | null;
        if (data?.type !== 'block' || !data.samples) return;
        handleBlock(data.samples);
      };
      node.onprocessorerror = () => {
        lastError = 'The audio worklet crashed. Switch to the ScriptProcessor fallback.';
        publishStatus();
      };
      workletNode = node;
      path = 'worklet';
      return true;
    } catch (error) {
      lastError = `AudioWorklet unavailable (${
        error instanceof Error ? error.message : String(error)
      }); using the ScriptProcessor fallback.`;
      return false;
    }
  }

  function buildScriptProcessorPath(ctx: AudioContext): void {
    // Deprecated, main-thread, and still the most reliable thing in Safari. See the
    // file header: this is a deliberate escape hatch, not an oversight.
    const node = ctx.createScriptProcessor(SCRIPT_PROCESSOR_BUFFER_SIZE, 1, 1);
    node.onaudioprocess = (event: AudioProcessingEvent) => {
      if (paused) return;
      const input = event.inputBuffer.getChannelData(0);
      // The event's buffer is reused by the implementation, so copy before re-chunking.
      rechunker.push(new Float32Array(input), handleBlock);
    };
    scriptNode = node;
    path = 'script-processor';
  }

  function connectGraph(ctx: AudioContext): void {
    const node: AudioNode | null = workletNode ?? scriptNode;
    if (!node) return;
    if (!sink) {
      sink = ctx.createGain();
      sink.gain.value = 0;
      sink.connect(ctx.destination);
    }
    node.connect(sink);
    if (source) source.connect(node);
  }

  function attachSource(ctx: AudioContext, stream: MediaStream): void {
    if (source) {
      try {
        source.disconnect();
      } catch {
        /* already gone */
      }
    }
    attachedStream = stream;
    trackEnded = false;
    watchTracks(stream);
    source = ctx.createMediaStreamSource(stream);
    const node: AudioNode | null = workletNode ?? scriptNode;
    if (node) source.connect(node);
  }

  async function doStart(stream: MediaStreamLike | null): Promise<void> {
    if (context && running) {
      // Already listening. A second call with a stream just swaps the source, which is
      // what happens after the mic is reacquired following an interruption.
      if (isRealMediaStream(stream)) attachSource(context, stream);
      await tryResumeContext();
      publishStatus();
      return;
    }

    const ctx = context ?? createAudioContext();
    context = ctx;

    // Resume FIRST, while the user gesture that called us is still on the stack.
    await tryResumeContext();

    if (!core) {
      loaded = await loadCore({
        sampleRate: ctx.sampleRate,
        params,
        blockSize,
        timeOriginMs: now(),
      });
      core = loaded.core;
      core.setParams(params);
      params = core.getParams();
    } else {
      core.reset?.();
    }

    rechunker.reset();
    if (!(await buildWorkletPath(ctx))) buildScriptProcessorPath(ctx);
    connectGraph(ctx);
    if (isRealMediaStream(stream)) attachSource(ctx, stream);

    watchContext(ctx);
    watchVisibility();

    running = true;
    paused = false;
    publishStatus();
  }

  return {
    async start(stream) {
      if (startInFlight) {
        await startInFlight;
        if (running) return;
      }
      startInFlight = doStart(stream).finally(() => {
        startInFlight = null;
      });
      try {
        await startInFlight;
      } catch (error) {
        lastError = error instanceof Error ? error.message : String(error);
        publishStatus();
        throw error;
      }
    },

    async stop() {
      running = false;
      paused = false;
      for (const undo of teardown.splice(0)) {
        try {
          undo();
        } catch {
          /* listener already detached */
        }
      }
      if (workletNode) {
        workletNode.port.onmessage = null;
        try {
          workletNode.disconnect();
        } catch {
          /* not connected */
        }
        workletNode = null;
      }
      if (scriptNode) {
        scriptNode.onaudioprocess = null;
        try {
          scriptNode.disconnect();
        } catch {
          /* not connected */
        }
        scriptNode = null;
      }
      if (source) {
        try {
          source.disconnect();
        } catch {
          /* not connected */
        }
        source = null;
      }
      if (sink) {
        try {
          sink.disconnect();
        } catch {
          /* not connected */
        }
        sink = null;
      }
      // The stream belongs to micManager; its tracks are deliberately left running.
      attachedStream = null;
      path = null;
      rechunker.reset();
      if (context && context.state !== 'closed') {
        try {
          await context.close();
        } catch {
          /* already closing */
        }
      }
      context = null;
      publishStatus();
    },

    pause() {
      if (paused) return;
      paused = true;
      // Tell the worklet to stop forwarding so a paused session costs no main-thread
      // work at all. State inside the core is untouched, per the interface contract.
      workletNode?.port.postMessage({ type: 'enable', value: false });
      rechunker.reset();
      publishStatus();
    },

    resume() {
      if (!paused) return;
      paused = false;
      workletNode?.port.postMessage({ type: 'enable', value: true });
      void tryResumeContext();
      publishStatus();
    },

    on(listener) {
      eventListeners.add(listener);
      return () => {
        eventListeners.delete(listener);
      };
    },

    getParams() {
      return core ? core.getParams() : params;
    },

    setParams(patch) {
      params = mergeRecognizerParams(core ? core.getParams() : params, patch);
      core?.setParams(params);
    },

    getStatus: status,

    onStatus(listener) {
      statusListeners.add(listener);
      return () => {
        statusListeners.delete(listener);
      };
    },

    tryResumeContext,
  };
}
